/** 작업 셸과 그 아래 화면이 읽는 작업 하나.
 *
 * **예시 데이터와 서버 작업이 같은 모양으로 들어온다.** 셸이 예시 표만 조회하던 동안
 * 서버에서 온 작업은 전부 "찾을 수 없음"으로 떨어졌다 — 표는 실제 `DetailJob.id` 로
 * 이동하는데 셸은 예시 목록에만 그 id 가 있는지 물었기 때문이다. 그래서 모양을 하나로
 * 두고, 서버 응답을 이 모양으로 옮긴다.
 *
 * ⚠️ 서버 작업에는 예시 썸네일 견본(`obj`)과 판 번호가 없을 수 있다. 없는 값은 **비워
 * 둔다** — 줄표나 `v0` 으로 채우면 화면이 없는 사실을 있는 것처럼 말한다. */

import {
  canRetryImage,
  isJobRunning,
  jobStatusLabel,
  recallBlockKind,
  recallBlockOf,
  type DetailJobStatus,
  type DetailJobView,
  type JobImageView,
  type StatusTone,
} from "@makit/shared";

/** 셸이 세우는 화면. **결과가 있으면 결과 모드(서브탭), 없으면 단계 모드**다(PRD 7.7.0). */
export type JobStage = "UPLOADING" | "PAY" | "MAKING" | "RESULT" | "REVIEW";

export interface ShellJob {
  id: string;
  name: string;
  tone: StatusTone;
  /** 상태 칩 문구. 정본은 shared 의 `jobStatusLabel` 이다.
   *
   * ⛔ **작업 바의 모노 줄에 이 값을 쓰지 마라.** 칩 라벨과 작업 바가 말하는 상태는 **집합이
   * 다르다** — 칩은 다섯(`초안`·`만드는 중`·`준비됨`·`내보냄`·`실패`)뿐인데 작업 바는
   * `올리는 중`·`결제 전` 까지 말해야 한다(PRD `7.7.0` 표). 이 값을 쓰면 **결제를 기다리는
   * 셀러에게 `초안` 이라고 말한다.** 그 줄은 아래 `jobBarMeta` 가 만든다. */
  status: string;
  /** 서버가 아는 상태. **모노 줄의 갈래가 여기서 나온다** — `stage` 로 가르면 안 된다
   *  (`jobBarMeta` 머리말). */
  serverStatus: DetailJobStatus;
  done: number;
  total: number;
  stage: JobStage;
  /** 예시 실루엣 클래스. 서버 작업은 결과물 미리보기를 아직 싣지 않아 없다. */
  obj?: string;
  /** `현재 v2`. 판이 없으면 없다. */
  versionLabel?: string;
}

/** 상태 → 셸이 세울 화면. **입구가 아니라 상태가 정한다**(PRD 7.7.0 동선 표).
 *
 * 예시 행과 서버 작업이 같은 함수를 쓴다. 예시 행에 `stage` 를 손으로 적어 두면
 * 칩은 `준비됨`인데 열리는 화면은 결제 화면인 행이 만들어진다.
 *
 * ⚠️ `DISCARDED`(사진 지워짐)에 해당하는 프레임이 목업에 없다. 결제 화면으로 보내면
 * 지워진 사진의 값을 받겠다고 말하게 되므로 올리는 화면으로 둔다. 확정은 design-manager
 * 소관이다. */
export function stageForStatus(status: DetailJobStatus, imageCount: number): JobStage {
  switch (status) {
    case "READY":
      return "RESULT";
    case "EXPORTED":
      return "REVIEW";
    case "PAID":
    case "PROCESSING":
      return "MAKING";
    // 전량 실패를 말하는 자리가 `만드는 중` 화면 안에 있다(`W-24` 의 전부 실패 구역).
    case "FAILED":
      return "MAKING";
    case "DISCARDED":
      return "UPLOADING";
    case "UPLOADED":
    default:
      // 한 장도 없으면 아직 올리는 중이다. 결제 화면을 열면 결제할 것이 없다.
      return imageCount === 0 ? "UPLOADING" : "PAY";
  }
}

/** 결과가 있으면 결과 모드(서브탭), 없으면 단계 모드(본문 하나 + `이력 ›`). */
export function hasResult(job: ShellJob): boolean {
  return job.stage === "RESULT" || job.stage === "REVIEW";
}

/** 셸이 단계마다 세우는 기본 화면. **주소와 그 화면의 이름이 한 줄에 함께 있다.**
 *
 * `W-50` 의 돌아가는 링크는 `stagePath` 로 가고 `stageBackLabel` 을 적는다(PRD `7.5.1a`
 * 「`W-50` 작업 바의 돌아가는 길」). 둘을 따로 짜면 말과 목적지가 갈린다. 실제로 갈려 있었다.
 * `READY` 가 `‹ 검토 · 고치기` 라고 적으면서 `W-31` 결과 확인을 열었다.
 *
 * `seg` 가 빈 문자열이면 작업 주소 그 자체(`W-24` 만드는 중)다.
 * `…중` 둘에만 `화면` 을 붙인다. 혼자 서면 상태 글자로 읽힌다(같은 절). */
const STAGE_SCREENS: Record<JobStage, { seg: string; backLabel: string }> = {
  UPLOADING: { seg: "upload", backLabel: "‹ 올리는 중 화면" },
  PAY: { seg: "pay", backLabel: "‹ 결제" },
  MAKING: { seg: "", backLabel: "‹ 만드는 중 화면" },
  RESULT: { seg: "result", backLabel: "‹ 결과 확인" },
  REVIEW: { seg: "review", backLabel: "‹ 검토 · 고치기" },
};

function stageScreen(job: ShellJob) {
  return STAGE_SCREENS[job.stage] ?? STAGE_SCREENS.REVIEW;
}

/** 단계 모드 작업의 기본 화면. */
export function stagePath(job: ShellJob): string {
  const { seg } = stageScreen(job);
  return seg ? `/jobs/${job.id}/${seg}` : `/jobs/${job.id}`;
}

/** `stagePath(job)` 가 여는 화면의 이름. `W-50` 작업 바 맨 좌의 돌아가는 링크 글자다. */
export function stageBackLabel(job: ShellJob): string {
  return stageScreen(job).backLabel;
}

/** 작업 바 우측의 모노 줄. **정본은 PRD `7.7.0` 의 표다**(목업 여섯 자리에서 실측).
 *
 * | 상태 | 줄 |
 * |---|---|
 * | `UPLOADED` · 사진 0장 | `사진 N장 · 올리는 중` |
 * | `UPLOADED` · 사진 있음 | `사진 N장 · 결제 전` |
 * | `PAID`·`PROCESSING` | `N / M장 · 만드는 중` |
 * | `READY` (`W-31`) | `N장 완료 · 현재 vX` |
 * | `EXPORTED` (`W-30`·`W-40`) | `사진 N장 · 현재 vX` |
 *
 * ## 왜 이 자리가 필요한가
 *
 * **작업 바에는 상태 칩이 없다**(목업 여섯 자리 전부. `jobbar` 의 자식이 셋뿐이다). 그래서
 * **이 줄이 상태를 말하는 유일한 자리**이고, 그 말이 틀리면 화면 어디에도 맞는 말이 없다.
 * 옛 구현은 칩 라벨(`jobStatusLabel`)을 그대로 붙여 **결제를 기다리는 작업에 `초안` 이라고
 * 적고 있었다** — 칩 라벨 다섯이 작업 바가 가르는 상태를 못 덮는다(`ShellJob.status` 주석).
 *
 * ⛔ **`stage` 로 갈래를 짜지 마라.** `stageForStatus` 가 `FAILED` 를 `MAKING` 으로 보내므로,
 * `stage` 기준이면 **전량 실패한 작업이 `0 / 8장 · 만드는 중` 이라고 말한다** — 끝난 작업이
 * 아직 돌고 있다는 뜻이 되어 지금보다 나쁘다.
 *
 * ⚠️ **`READY` 와 `EXPORTED` 를 상태만으로 가르지 않는다.** 같은 `준비됨` 작업이 `W-31`
 * (결과 확인)과 `W-30`(검토·고치기) 둘 다에 설 수 있어서 **프레임, 곧 주소가 가른다.**
 * 상태로 가르면 `준비됨` 인 작업의 검토 화면이 `7장 완료` 라고 말한다.
 *
 * ⚠️ **`FAILED`·`DISCARDED` 는 정본이 없다.** 그 화면이 목업에 없다(PRD `7.13` D-39).
 * 지금은 칩 라벨을 그대로 쓰고, **그것이 임시라는 것이 이 주석이다.** */
export function jobBarMeta(job: ShellJob, segment: string): string {
  const version = job.versionLabel ? ` · 현재 ${job.versionLabel}` : "";
  switch (job.serverStatus) {
    case "READY":
    case "EXPORTED":
      // 빈 segment 는 `/jobs/:jobId` 로 막 들어와 `StageIndex` 가 아직 옮기기 전이다.
      // 그 찰나에 `W-31` 로 갈 작업이므로 결과 확인 쪽으로 읽는다.
      return segment === "result" || segment === ""
        ? `${job.done}장 완료${version}`
        : `사진 ${job.done}장${version}`;
    case "PAID":
    case "PROCESSING":
      return `${job.done} / ${job.total}장 · 만드는 중`;
    case "UPLOADED":
      // 한 장도 안 올라왔으면 아직 올리는 중이다(`stageForStatus` 와 같은 판정축).
      return job.total === 0
        ? `사진 ${job.total}장 · 올리는 중`
        : `사진 ${job.total}장 · 결제 전`;
    case "FAILED":
    case "DISCARDED":
    default:
      // 정본이 없는 자리. 칩 라벨을 빌려 쓴다 — **목업이 그 화면을 그리면 위 표로 옮긴다.**
      return `${job.done} / ${job.total}장 · ${job.status}`;
  }
}

/** 서버 응답 → 셸이 읽는 모양. 상태 칩·장수·단계를 여기서 한 번만 계산한다. */
export function toShellJob(view: DetailJobView): ShellJob {
  const chip = jobStatusLabel(view.status);
  const done = view.images.filter((image) => image.status === "DONE").length;
  return {
    id: view.id,
    // 상품명이 없으면 작업 번호를 대신 쓰지 않는다(PRD 7.5.9 규칙 3).
    name: view.productName ?? "이름 없는 작업",
    tone: chip.tone,
    status: chip.label,
    serverStatus: view.status,
    done,
    total: view.images.length,
    stage: stageForStatus(view.status, view.images.length),
    ...(view.currentVersionNo !== null ? { versionLabel: `v${view.currentVersionNo}` } : {}),
  };
}

// ── `W-21` 못 만든 장 다시 만들기 ──────────────────────────────────────────────
//
// 화면(`screens/stages/Uploading.tsx`)이 그리고, **판정은 여기 한 자리다.** 판정의 재료는
// 전부 shared 다(`canRetryImage` · `recallBlockOf` · `recallBlockKind`). 앱 `A-31` 이 같은
// 함수들로 같은 장에서 같은 시트를 연다(`apps/mobile/src/app/detail/result.tsx` 의
// `canOpenRetry`, `apps/mobile/src/features/job/retry-sheet.tsx`).

/** `W-21` 오른쪽 패널의 갈래. 앱 `A-25b`(되살린다) · `A-25c`(지금은 제안하지 않는다)와 같다. */
export type ReviveBranch = "REVIVE" | "BLOCKED";

/** 이 장의 `↻` 가 무엇을 여는가. `null` 이면 `↻` 를 두지 않는다.
 *
 * ⛔ **못 만든 장(`FAILED`)만 연다. 만든 장(`DONE`)은 열지 않는다.** 만든 장 다시 만들기
 * (앱 `A-25`)는 장당 ₩500 을 새로 받는 일인데, 지금 서버는 새 결제를 받지 않고 청구 항목만
 * 붙여 **승인한 금액을 넘는 미청구**가 생긴다(`docs/DATA_DICTIONARY.md` M-14 미반영). 그
 * 갈래가 열릴 때까지 웹은 이 첫 줄로 막는다. ⛔ 이 줄을 지우면 `DONE` 장에 `↻` 가 서고
 * 패널이 `REVIVE` 로 그 장을 받는다. 서버 `/process` 는 `DONE` 장에 공급자를 부르지 않지만
 * 화면은 「다시 만들어도 더 내실 금액이 없습니다」라는, 그 장에는 거짓인 말을 하게 된다.
 *
 * **막힌 장도 연다**(`BLOCKED`). 왜 못 하는지 읽을 자리가 그 패널뿐이다(앱 `result.tsx` 의
 * `canOpenRetry` 와 같은 판단). ⛔ 다만 작업이 돌고 있으면 열지 않는다. 몇 초 뒤 끝날 일에
 * `다시 되는 때 / 미정` 을 적지 않는다(`recallBlockKind` 의 `BUSY`).
 *
 * ⭐ **시계는 서버가 준 것이다**(`job.servedAt`). ⛔ `Date.now()` 로 만들지 마라. 원본 보관
 * 기한 판정이 그 값으로 돌고, 기기 시계는 셀러가 바꿀 수 있다. **두 함수에 같은 값을 넘긴다.** */
export function reviveBranchOf(image: JobImageView, job: DetailJobView): ReviveBranch | null {
  if (image.status !== "FAILED") return null;
  if (isJobRunning(job)) return null;
  const now = job.servedAt;
  const blocked = recallBlockOf(image, job, now);
  if (blocked !== null) return recallBlockKind(blocked) === "BUSY" ? null : "BLOCKED";
  return canRetryImage(image, job, now) ? "REVIVE" : null;
}

/** 목업 `W-21` 의 접는 규칙: **최대 4줄 + 요약 한 줄**, `실패 → 올리는 중 → 대기 → 완료` 순. */
export const UPLOAD_STAGE_VISIBLE_ROWS = 4;

/** 채우는 순서. 모르는 상태는 `대기` 자리에 둔다(`imageStatusLabel` 의 기본 갈래와 같다). */
function fillOrder(status: JobImageView["status"]): number {
  switch (status) {
    case "FAILED":
      return 0;
    case "PROCESSING":
      return 1;
    case "DONE":
      return 3;
    case "PENDING":
    default:
      return 2;
  }
}

export interface UploadStageRows {
  /** 그릴 행. 채우는 순서대로다. */
  visible: JobImageView[];
  /** 접은 장 수. **언제나 완료 장이다**(아래 머리말). */
  foldedDone: number;
}

/** `W-21` 목록의 행과 접은 수.
 *
 * ⚠️ **손이 필요한 장은 접지 않는다.** 목업은 네 줄이지만 못 만든 장이 다섯이면 다섯째의 `↻`
 * 가 접힌 줄 안에 숨는다. 그래서 완료가 아닌 장이 넷을 넘으면 그 수만큼 그리고, 접는 것은
 * 완료 장뿐이다. 그래야 요약 줄의 `완료 N장은 접었습니다` 가 참이다. */
export function uploadStageRows(images: readonly JobImageView[]): UploadStageRows {
  const sorted = [...images].sort(
    (a, b) => fillOrder(a.status) - fillOrder(b.status) || a.sortOrder - b.sortOrder,
  );
  const handsOn = sorted.filter((image) => image.status !== "DONE").length;
  const visible = sorted.slice(0, Math.max(UPLOAD_STAGE_VISIBLE_ROWS, handsOn));
  return { visible, foldedDone: sorted.length - visible.length };
}

/** 목업 `W-21` 의 요약 줄(`완료 4장은 접었습니다 · 전체 8장`). 접은 장이 없으면 줄을 안 세운다. */
export function uploadStageFoldLine(rows: UploadStageRows, total: number): string | null {
  return rows.foldedDone > 0 ? `완료 ${rows.foldedDone}장은 접었습니다 · 전체 ${total}장` : null;
}

/** `W-21` 이 진행 화면(올리는 중 표)을 그리는가.
 *
 * 사진 0장 · 사진 지워짐(`DISCARDED`) 작업은 `false` 다. 그 두 상태는 진행 표 대신 빈 상태
 * (`Uploading.tsx` 의 `UploadEmpty`, 「내 작업」 버튼 하나)로 간다. 예시 화면은 2026-10-05 에
 * 걷었다(PRD `7.13` D-39 의 빈자리는 문구 확정 기록이 남았다). */
export function uploadStageShowsJob(job: DetailJobView): boolean {
  return job.images.length > 0 && job.status !== "DISCARDED";
}
